// Shared layout for the product pages (/workspace-feature, /creator-feature, /youtube-feature,
// /local-feature). Each page passes its copy and media; optional sections are skipped when absent.
import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import ChannelMarquee from '../site/ChannelMarquee';
import CtaBand from '../site/CtaBand';
import Seo from '../site/Seo';
import { APP_PRODUCTS as PRODUCTS } from '../site/siteLinks';
import './productPage.css';

const CtaLink = ({ to = '/signup', children }) => (
  <Link to={to} className="mk-link">
    {children} <FontAwesomeIcon icon={faArrowRight} className="mk-btn-arrow" />
  </Link>
);

export default function ProductPage({ path, seo, hero, marquee, benefits, workflow, rows, caseStudy }) {
  const product = PRODUCTS.find(p => p.to === path);
  const others = PRODUCTS.filter(p => p.to !== path);

  return (
    <>
      <Seo title={seo.title} description={seo.description} />

      {/* Hero */}
      <section className="prod-hero mk-hero-bg">
        <div className="mk-container prod-hero-grid">
          <div>
            <p className="mk-eyebrow"><FontAwesomeIcon icon={product.icon} /> {product.title}</p>
            <h1 className="mk-h1 prod-title">{hero.title}</h1>
            <p className="mk-lead">{hero.subtitle}</p>
            <div className="mk-actions">
              <Link to="/signup" className="mk-btn mk-btn--primary">
                Start for free today <FontAwesomeIcon icon={faArrowRight} className="mk-btn-arrow" />
              </Link>
              <Link to="/pricing" className="mk-btn">See pricing</Link>
            </div>
            {hero.note && <p className="mk-note">{hero.note}</p>}
          </div>
          <div className="mk-glow">
            <div className="mk-frame prod-hero-media">
              <img src={hero.image} alt={hero.imageAlt} width={hero.width} height={hero.height} />
            </div>
          </div>
        </div>
      </section>

      {marquee && (
        <div className="prod-proof">
          <ChannelMarquee caption="Bump AI will watch any video and deliver insights across all industries." />
        </div>
      )}

      {benefits && (
        <section className="mk-section">
          <div className="mk-container">
            <div className="mk-section-head">
              <h2 className="mk-h2">{benefits.title}</h2>
            </div>
            <div className="mk-grid mk-grid--3">
              {benefits.items.map(b => (
                <div key={b.title} className="mk-card prod-benefit">
                  <span className="mk-icon"><FontAwesomeIcon icon={b.icon} /></span>
                  <h3 className="mk-h3">{b.title}</h3>
                  <p className="mk-text">{b.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* How it works: the product video beside its key features */}
      <section className="mk-section">
        <div className="mk-container">
          <div className="prod-workflow-head">
            <h2 className="mk-h2">{workflow.title}</h2>
            <p className="mk-lead">{workflow.description}</p>
          </div>
          <div className="prod-workflow">
            <div className="mk-frame prod-workflow-media">
              <video src={workflow.video} poster={workflow.poster} autoPlay muted loop playsInline aria-label={`${product.title} demo`} />
            </div>
            <ol className="prod-steps">
              {workflow.features.map((f, i) => (
                <li key={f.title} className="prod-step">
                  <span className="prod-step-num">{String(i + 1).padStart(2, '0')}</span>
                  <div>
                    <h3 className="mk-h3">{f.title}</h3>
                    <p className="mk-text">{f.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Feature spotlights, alternating sides */}
      <section className="mk-section prod-rows">
        <div className="mk-container">
          {rows.map((row, i) => (
            <div key={row.title} className={`prod-row${i % 2 ? ' prod-row--flip' : ''}`}>
              <div className="prod-row-text">
                <h2 className="mk-h2 prod-row-title">{row.title}</h2>
                <p className="mk-lead">{row.text}</p>
                <CtaLink>{row.cta}</CtaLink>
              </div>
              <div className="mk-frame prod-row-media">
                <img src={row.image} alt={row.alt} loading="lazy" width="1080" height="1080" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {caseStudy && (
        <section className="mk-section">
          <div className="mk-container">
            <div className="prod-case">
              <div className="prod-case-media">
                <img src={caseStudy.image} alt={caseStudy.imageAlt} loading="lazy" />
              </div>
              <div className="prod-case-body">
                <p className="mk-eyebrow">Case study</p>
                <h2 className="mk-h2">{caseStudy.title}</h2>
                <p className="mk-lead">{caseStudy.text}</p>
                <dl className="prod-stats">
                  {caseStudy.stats.map(s => (
                    <div key={s.value} className="prod-stat">
                      <dt>{s.value}</dt>
                      <dd>{s.label}</dd>
                    </div>
                  ))}
                </dl>
                <CtaLink>Start for free today</CtaLink>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* The rest of the product */}
      <section className="mk-section mk-section--tight">
        <div className="mk-container">
          <h2 className="mk-h3 prod-more-title">More from Bumpups</h2>
          <div className="mk-grid mk-grid--3">
            {others.map(p => (
              <Link key={p.to} to={p.to} className="mk-card prod-more">
                <span className="mk-icon"><FontAwesomeIcon icon={p.icon} /></span>
                <span>
                  <span className="prod-more-name">{p.title}</span>
                  <span className="prod-more-blurb">{p.blurb}</span>
                </span>
                <FontAwesomeIcon icon={faArrowRight} className="prod-more-arrow" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
